/**
 * Check editing (route `/checks/:id/edit`; Feature: granular-crud-api, tasks 14.1, 14.2; Requirements
 * 11.1, 11.6, 11.7, 11.8; Design: §11).
 *
 * The Check is loaded first — through the same {@link useResource} the detail page uses — because the
 * form needs its current values *and* its `origin`. Origin decides what this edit *means*, so
 * {@link ProvenanceNotice} states it above the form before the user saves: a manifest-managed Check
 * leaves the deployment manifest's reconciliation permanently once written through the API (Req 11.8),
 * and a Terraform-managed one transfers to the API but stays declared in HCL, so the edit becomes drift
 * (terraform-provider Req 13.1). The Terraform case additionally requires an explicit acknowledgement
 * before the submit is live (Req 13.3), held here because the page owns the write.
 *
 * `PATCH /v1/checks/{id}` runs through {@link useWrite}: a failure renders the returned error and keeps
 * the draft (Req 11.6); a success navigates to the Check's detail page, which re-reads it — except when
 * the response reports a transfer *from Terraform*, which is confirmed in place (Req 13.5) rather than
 * thrown away by a navigation.
 *
 * A viewer gets the same form read-only (Req 11.7) — the values are theirs to see, the submit is not.
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import type { GetCheckResponse } from '../api/types';
import { useApiClient } from '../api/use-api-client';
import { useConfig } from '../config-context';
import { useCanWrite } from '../auth/use-can-write';
import {
  ProvenanceNotice,
  ProvenanceTransferNotice,
  requiresAcknowledgement,
  transferredFromTerraform,
} from '../components/ProvenanceNotice';
import { CheckForm } from '../components/CheckForm';
import { ErrorState, LoadingState } from '../components/LoadStates';
import { useResource } from '../lib/use-resource';
import { useWrite } from '../lib/use-write';

export function CheckEditPage(): JSX.Element {
  const client = useApiClient();
  // The deployment's run locations (Req 8.2), for the form's location picker. Read here rather than
  // in CheckForm so the form keeps taking no context dependency.
  const { runnerRegions } = useConfig();
  const canWrite = useCanWrite();
  const navigate = useNavigate();
  const { id } = useParams();
  const checkId = id ?? '';
  const write = useWrite();
  /** The Terraform acknowledgement (Req 13.3). Ignored for every other origin. */
  const [acknowledged, setAcknowledged] = useState(false);
  /** Set when the write reported an Ownership_Transfer away from Terraform (Req 13.5). */
  const [transferred, setTransferred] = useState(false);

  const loaded = useResource<GetCheckResponse>(() => client.getCheck(checkId), [client, checkId]);

  if (loaded.status === 'loading') {
    return (
      <section>
        <h1>Edit check</h1>
        <LoadingState label="Loading check…" />
      </section>
    );
  }

  if (loaded.status === 'error') {
    return (
      <section>
        <h1>Edit check</h1>
        <ErrorState
          summary="Could not load this check."
          message={loaded.message}
          isAuthError={loaded.isAuthError}
        />
      </section>
    );
  }

  const check = loaded.data.check;

  return (
    <section>
      <h1>Edit {check.logicalName}</h1>
      {!canWrite ? (
        <p role="note" className="muted" data-testid="read-only-notice">
          Your role has read-only access, so this check cannot be changed from the dashboard.
        </p>
      ) : null}
      <CheckForm
        mode="edit"
        availableLocations={runnerRegions}
        initial={check}
        readOnly={!canWrite}
        submitting={write.state.submitting}
        fieldErrors={write.state.fieldErrors}
        errorMessage={write.state.errorMessage}
        notice={
          canWrite ? (
            transferred ? (
              // The loaded Check still reads `terraform` — nothing re-read it — so keeping the warning
              // up would contradict the confirmation. The transfer already happened; say that instead.
              <ProvenanceTransferNotice />
            ) : (
              <ProvenanceNotice
                origin={check.origin}
                entity="check"
                acknowledged={acknowledged}
                onAcknowledge={setAcknowledged}
              />
            )
          ) : undefined
        }
        submitDisabled={requiresAcknowledgement(check.origin) && !acknowledged}
        onSubmit={(patch) =>
          void write.run(
            () => client.updateCheck(checkId, patch),
            (response) => {
              // A Terraform hand-over is confirmed in place (Req 13.5); every other write navigates to
              // the detail page as before, which re-reads the Check.
              if (transferredFromTerraform(response)) setTransferred(true);
              else navigate(`/checks/${encodeURIComponent(checkId)}`);
            },
          )
        }
        onCancel={() => navigate(`/checks/${encodeURIComponent(checkId)}`)}
      />
    </section>
  );
}
